import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Lock, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Reset link is missing or malformed"); return; }
    if (next.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: next });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#0A0A0A] text-white">
      <div className="hidden md:block relative overflow-hidden border-r border-white/10">
        <img
          src="https://images.pexels.com/photos/35688546/pexels-photo-35688546.jpeg?auto=compress&cs=tinysrgb&w=1200"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/70 to-black" />
        <div className="relative p-12 flex flex-col h-full">
          <Link to="/login" className="text-xs uppercase tracking-[0.3em] text-[#38BDF8] font-bold flex items-center gap-2" data-testid="back-login-link">
            <ArrowLeft size={14} /> Back to login
          </Link>
          <div className="mt-auto">
            <h2 className="font-display text-5xl font-black uppercase tracking-tighter leading-[0.9]">
              Set a new<br />
              <span className="text-[#38BDF8]">password.</span>
            </h2>
            <p className="text-[#A3A3A3] mt-6 max-w-sm">
              Pick something strong and unique — minimum 6 characters.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <div className="w-full max-w-md" data-testid="reset-password-page">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Recovery · Step 02</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tighter mt-3">New password</h1>

          {!token && (
            <div className="mt-8 border border-red-500/30 bg-red-500/5 p-5 text-sm" data-testid="reset-token-missing">
              <p>This reset link is missing the token. Go back to <Link to="/forgot-password" className="text-[#38BDF8] font-bold hover:underline">forgot password</Link> to request a new one.</p>
            </div>
          )}

          {token && done && (
            <div className="mt-8 border border-[#38BDF8]/30 bg-[#38BDF8]/5 p-5" data-testid="reset-success">
              <div className="inline-flex items-center gap-2 text-[#38BDF8] font-bold text-sm mb-2">
                <CheckCircle2 size={16} /> Password updated
              </div>
              <p className="text-sm text-white/90">
                Your password has been reset. Redirecting you to login…
              </p>
            </div>
          )}

          {token && !done && (
            <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="reset-password-form">
              <PwField label="New password" value={next} onChange={setNext} show={show} onToggle={() => setShow(!show)} testId="reset-new" />
              <PwField label="Confirm new password" value={confirm} onChange={setConfirm} show={show} onToggle={() => setShow(!show)} testId="reset-confirm" />
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-4 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="reset-password-submit"
              >
                <Lock size={14} /> {loading ? "Updating…" : "Reset password"}
              </button>
              <div className="text-sm text-[#A3A3A3]">
                <Link to="/login" className="text-[#38BDF8] font-bold hover:underline" data-testid="reset-back-login">Back to login</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function PwField({ label, value, onChange, show, onToggle, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">{label}</label>
      <div className="flex items-center gap-2 border-b border-white/20 focus-within:border-[#38BDF8]">
        <input
          type={show ? "text" : "password"}
          required minLength={6}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="new-password"
          className="flex-1 bg-transparent outline-none py-3 text-lg"
          data-testid={testId}
        />
        <button type="button" onClick={onToggle} className="text-[#A3A3A3] hover:text-white p-1" tabIndex={-1} data-testid={`${testId}-toggle`}>
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}
